import React, { useEffect, useState } from "react";
import { ChevronDown, FolderKanban, Check, Plus, User, Lock } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import NewProjectDialog from "./NewProjectDialog";
import PricingDialog from "./PricingDialog";
import { useActiveProject } from "@/lib/ActiveProjectContext";
import { useAuth } from "@/lib/AuthContext";
import { useCredits } from "@/lib/CreditsContext";

export default function ProjectSwitcher({ currentId, onSelect }) {
  const [projects, setProjects] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const { project } = useActiveProject();
  const { user } = useAuth();
  const { lifetimePurchased } = useCredits();
  const locked = !!user && lifetimePurchased <= 0;

  const refresh = () => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  };

  useEffect(() => {
    refresh();
  }, []);

  // refresh list whenever active project changes (a fresh one was created)
  useEffect(() => {
    refresh();
  }, [project?.id]);

  const current = projects.find((p) => p.id === currentId) || projects[0];
  const custom = projects.filter((p) => p.custom);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          data-testid="project-switcher"
          className="inline-flex items-center gap-2 bg-slate-900/70 border border-slate-800 hover:border-cyan-500/60 px-3 py-1.5 rounded-sm transition-colors text-left group"
        >
          <FolderKanban className="h-3.5 w-3.5 text-cyan-400" />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 group-hover:text-cyan-400">
              Active Project
            </div>
            <div className="text-xs font-medium text-slate-100 truncate max-w-[260px]">
              {current?.name || "—"}
            </div>
          </div>
          <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="bg-slate-950 border-slate-800 text-slate-100 rounded-sm w-[380px]"
        >
          <DropdownMenuItem
            onClick={() => (locked ? setPricingOpen(true) : setNewOpen(true))}
            data-testid={locked ? "new-project-locked" : "new-project-menu-item"}
            className="flex items-center gap-2 cursor-pointer focus:bg-cyan-500/10 focus:text-cyan-300 border-b border-slate-800 rounded-none py-2.5"
          >
            {locked ? (
              <Lock className="h-3.5 w-3.5 text-cyan-400" />
            ) : (
              <Plus className="h-3.5 w-3.5 text-cyan-400" />
            )}
            <span className="text-sm font-medium text-cyan-300">
              {locked ? "Unlock — choose a plan" : "Add Live Project"}
            </span>
            <span className="ml-auto text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
              {locked ? "starts at $10" : "enter your data"}
            </span>
          </DropdownMenuItem>

          {custom.length > 0 ? (
            <>
              <div className="px-2 pt-2 pb-1 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1">
                <User className="h-3 w-3" /> Your Projects
              </div>
              {custom.map((p) => (
                <ProjectRow key={p.id} p={p} current={currentId} onSelect={onSelect} />
              ))}
            </>
          ) : (
            <div
              data-testid="project-switcher-empty"
              className="px-3 py-3 text-xs text-slate-500"
            >
              No projects yet — add your first live project.
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <NewProjectDialog open={newOpen} onOpenChange={setNewOpen} />
      <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
    </>
  );
}

function ProjectRow({ p, current, onSelect }) {
  return (
    <DropdownMenuItem
      data-testid={`project-option-${p.id}`}
      onClick={() => onSelect(p.id)}
      className="flex items-start gap-2 cursor-pointer focus:bg-slate-900 focus:text-cyan-300"
    >
      <Check
        className={`h-3.5 w-3.5 mt-0.5 ${
          p.id === current ? "text-cyan-400" : "text-transparent"
        }`}
      />
      <div className="flex-1">
        <div className="text-sm font-medium">{p.name}</div>
        <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">
          {p.contract_type} · {p.client} · ${p.value_usd_mn} mn
        </div>
      </div>
    </DropdownMenuItem>
  );
}
